import { isDefined } from '@sgme/fp';

import type { ReceivedOptionQuoteReply } from '@/api/option/type.ts';
import type { LegsData, MultipassLegId, OptionQuote } from '@/store/option/state.ts';
import { parseDecimal } from '@/utils/number.ts';
import { objectEntries } from '@/utils/object.ts';
import type { QuoteWithPrice } from '@/utils/rfs/type.ts';
import { toPredealCheck } from '@/utils/rfs/utils.ts';

export function createQuote(message: QuoteWithPrice<ReceivedOptionQuoteReply>): OptionQuote {
  const { Timestamp: timestamp, RequestId: requestId, OptionQuoteReply: reply } = message;

  const { Ccy1, Ccy2 } = reply;
  const legsData: [MultipassLegId, LegsData][] = objectEntries(reply.LegsData).map(([legId, legData]) => {
    const { Greeks, MarginData, MarketData, MidVolatility, PremiumData, StrikeData, Solving } = legData;
    return [
      legId,
      {
        greeks: {
          deltaCcy1: parseDecimal(Greeks.DeltaCcy1),
          deltaCcy2: parseDecimal(Greeks.DeltaCcy2),
          gammaCcy1: parseDecimal(Greeks.GammaCcy1),
          gammaCcy2: parseDecimal(Greeks.GammaCcy2),
          thetaCcy1: parseDecimal(Greeks.ThetaCcy1),
          thetaCcy2: parseDecimal(Greeks.ThetaCcy2),
          vegaCcy1: parseDecimal(Greeks.VegaCcy1),
          vegaCcy2: parseDecimal(Greeks.VegaCcy2),
        },
        premiumData: {
          askAmountCcy1: parseDecimal(PremiumData.AskAmountCcy1),
          askAmountCcy2: parseDecimal(PremiumData.AskAmountCcy2),
          bidAmountCcy1: parseDecimal(PremiumData.BidAmountCcy1),
          bidAmountCcy2: parseDecimal(PremiumData.BidAmountCcy2),
          bidPercentCcy1: parseDecimal(PremiumData.BidPercentCcy1),
          bidPercentCcy2: parseDecimal(PremiumData.BidPercentCcy2),
          askPercentCcy1: parseDecimal(PremiumData.AskPercentCcy1),
          askPercentCcy2: parseDecimal(PremiumData.AskPercentCcy2),
          askPpsCcy1: parseDecimal(PremiumData.AskPpsCcy1),
          askPpsCcy2: parseDecimal(PremiumData.AskPpsCcy2),
          bidPpsCcy1: parseDecimal(PremiumData.BidPpsCcy1),
          bidPpsCcy2: parseDecimal(PremiumData.BidPpsCcy2),
        },
        marginData: isDefined(MarginData)
          ? {
              askMarkupCcy1: parseDecimal(MarginData.AskMarkupCcy1),
              askMarkupCcy2: parseDecimal(MarginData.AskMarkupCcy2),
              bidMarkupCcy1: parseDecimal(MarginData.BidMarkupCcy1),
              bidMarkupCcy2: parseDecimal(MarginData.BidMarkupCcy2),
              askMarkupInBp: parseDecimal(MarginData.AskMarkupInBp),
              bidMarkupInBp: parseDecimal(MarginData.BidMarkupInBp),
            }
          : undefined,
        marketData: {
          forward: {
            bid: parseDecimal(MarketData.Forward.Bid),
            ask: parseDecimal(MarketData.Forward.Ask),
          },
          swapPoint: {
            bid: parseDecimal(MarketData.SwapPoint.Bid),
            ask: parseDecimal(MarketData.SwapPoint.Ask),
          },
          volatility: {
            bid: parseDecimal(MarketData.Volatility.Bid),
            ask: parseDecimal(MarketData.Volatility.Ask),
          },
          zeroCouponCcy1: parseDecimal(MarketData.ZeroCouponCcy1),
          zeroCouponCcy2: parseDecimal(MarketData.ZeroCouponCcy2),
        },
        midVolatility: isDefined(MidVolatility)
          ? {
              atmVolatilityMid: parseDecimal(MidVolatility.AtmVolatilityMid),
              smiledVolatilityMid: parseDecimal(MidVolatility.SmiledVolatilityMid),
            }
          : undefined,
        strikeData: {
          absoluteStrikePrice: parseDecimal(StrikeData.AbsoluteStrikePrice),
          deltaStrikePrice: StrikeData.DeltaStrikePrice,
          solvedStrike: parseDecimal(Solving),
        },
      } satisfies LegsData,
    ];
  });

  const hedgesData = reply.Hedge.map((hedge) => {
    return {
      notionalCurrency: hedge.NotionalCurrency,
      notionalAmount: isDefined(hedge.NotionalAmount) ? parseDecimal(hedge.NotionalAmount) : undefined,
      maturityDate: hedge.MaturityDate,
      rateMid: parseDecimal(hedge.RateMid ?? '0'),
    };
  });
  return {
    requestId,
    backTimestamp: timestamp,
    frontTimestamp: message.workerTimestamp,
    contribution: reply.QuoteContribution ?? 'Manual',
    tiering: reply.DistributionStream ?? 'STREAMDEFAULT',
    // traderId: reply.TraderId,
    quoteId: reply.QuoteId,
    quoteType: reply.QuoteType,
    rfsWindow: reply.RfsWindow,
    predealChecks: reply.PredealChecks.map(toPredealCheck),
    ccy1: {
      askAmount: parseDecimal(Ccy1.AskAmount),
      bidAmount: parseDecimal(Ccy1.BidAmount),
      askPercent: parseDecimal(Ccy1.AskPercent),
      bidPercent: parseDecimal(Ccy1.BidPercent),
      askPps: parseDecimal(Ccy1.AskPps),
      bidPps: parseDecimal(Ccy1.BidPps),
      deltaCcy: parseDecimal(Ccy1.DeltaCcy),
      vegaCcy: parseDecimal(Ccy1.VegaCcy),
      gammaCcy: parseDecimal(Ccy1.GammaCcy),
      thetaCcy: parseDecimal(Ccy1.ThetaCcy),
      askMarkup: parseDecimal(Ccy1.AskMarkup),
      bidMarkup: parseDecimal(Ccy1.BidMarkup),
    },
    ccy2: {
      askAmount: parseDecimal(Ccy2.AskAmount),
      bidAmount: parseDecimal(Ccy2.BidAmount),
      askPercent: parseDecimal(Ccy2.AskPercent),
      bidPercent: parseDecimal(Ccy2.BidPercent),
      askPps: parseDecimal(Ccy2.AskPps),
      bidPps: parseDecimal(Ccy2.BidPps),
      deltaCcy: parseDecimal(Ccy2.DeltaCcy),
      vegaCcy: parseDecimal(Ccy2.VegaCcy),
      gammaCcy: parseDecimal(Ccy2.GammaCcy),
      thetaCcy: parseDecimal(Ccy2.ThetaCcy),
      askMarkup: parseDecimal(Ccy2.AskMarkup),
      bidMarkup: parseDecimal(Ccy2.BidMarkup),
    },
    spot: {
      bid: parseDecimal(reply.Spot?.Bid),
      ask: parseDecimal(reply.Spot?.Ask),
    },
    hedgesData,
    legsData: Object.fromEntries(legsData),
  };
}
